import * as React from "react";

/**
 * In-memory tracker for rate-limit cooldowns per endpoint.
 * When the API returns 429 with Retry-After, we record the expiry time
 * so components can disable submit buttons during the cooldown.
 */

const cooldownUntilByKey = new Map<string, number>();

/** Normalize URL to a key (path without query for grouping). */
function getKeyForUrl(url: string | undefined): string {
  if (!url || typeof url !== "string") return "default";
  try {
    const path = url.split("?")[0];
    return path || "default";
  } catch {
    return "default";
  }
}

/**
 * Record that the given endpoint is rate-limited until the given timestamp (ms).
 * Called from the Axios error interceptor when a 429 is received.
 */
export function setRateLimitUntil(urlOrKey: string, retryAfterSeconds: number): void {
  const key = getKeyForUrl(urlOrKey);
  const until = Date.now() + retryAfterSeconds * 1000;
  cooldownUntilByKey.set(key, until);
}

/**
 * Get the timestamp (ms) until which the endpoint is rate-limited, or null if not limited.
 */
export function getRateLimitUntil(urlOrKey: string): number | null {
  const key = getKeyForUrl(urlOrKey);
  const until = cooldownUntilByKey.get(key);
  if (until == null) return null;
  if (Date.now() >= until) {
    cooldownUntilByKey.delete(key);
    return null;
  }
  return until;
}

/**
 * Check if the endpoint is currently rate-limited.
 */
export function isRateLimited(urlOrKey: string): boolean {
  return getRateLimitUntil(urlOrKey) != null;
}

/**
 * React hook: returns whether the given endpoint is currently rate-limited,
 * and the seconds remaining. Re-renders when the countdown updates (every second).
 */
export function useRateLimitBlock(endpoint: string): {
  isBlocked: boolean;
  secondsRemaining: number;
} {
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    const until = getRateLimitUntil(endpoint);
    if (until == null) return undefined;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [endpoint]);

  const until = getRateLimitUntil(endpoint);
  const secondsRemaining =
    until != null ? Math.max(0, Math.ceil((until - now) / 1000)) : 0;
  const isBlocked = secondsRemaining > 0;

  return { isBlocked, secondsRemaining };
}
